import React, { PureComponent, ReactNode, CSSProperties } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { Button } from '@tencent/spaui';
import { Toolbar as PTPToolbar } from '@tencent/ptp-fe-common';
import CustomColumn, { CustomColumnProps } from '@/business-components/custom-column';
import BatchToolbar, { BatchToolbarProps } from '@/common-components/batch-toolbar';
import Download from '@/common-components/Download';
import GuideTip from '@/common-components/guide-tip';
import { FLAGS } from '@/constants/Constants';
import './assets/less/index.scoped.less';

type DownType = 'default' | 'multi' | 'newSeenOld';
interface DownLoadProps {
  isCrossAgencyDownLoad?: boolean,
  customTagsName?: string,
  // 下载类型，支持 default, multi, newSeenOld,
  downloadType?: DownType,
  /* 组件类名 */
  className?: string,
  /* 样式 */
  style?: CSSProperties,
  /* 子元素 */
  children?: any;
  /* 按钮文案 */
  label?: string,
  /* 下载类型 */
  type?: string,
  /* 下载参数 */
  param?: any,
  /* 获取下载参数 */
  getParams?: () => void;
  /* 服务商UID */
  agencyUid?: number,
  /* 需要ID，这个参数已经不再需要，应该删掉 */
  needId?: boolean,
  /* 提示 */
  conditions?: boolean,
  /* 提示文案 */
  conditionsMsg?: string,
  /* 提示文案方法 */
  conditionsFunc?: () => string | void;
  /* 是否需要return_fields */
  needReturnFields?: boolean,
  /* 上报key，默认为空 */
  reportKey?: Record<string, string>,
  /* 下载文件名称 */
  nameRender?: (name: string) => string,
}
export interface ToolbarProps {
  /* 组件类名 */
  className?: string,
  /* 左侧内容 */
  left?: ReactNode,
  /* 右侧内容 */
  right?: ReactNode | [ReactNode, string],
  /* 左侧区域类名 */
  leftClassName?: string;
  /* 右侧区域类名 */
  rightClassName?: string;
  /* 自定义列选项 */
  columnsOptions?: CustomColumnProps,
  /* 批量操作选项 */
  batchOptions?: Omit<BatchToolbarProps, 'show' | 'onClose'>,
  /* 下载数据选项 */
  downloadOptions?: DownLoadProps,
  /* 刷新数据选项 */
  reloadOptions?: ReloadProps,
  /* 表格ref */
  listRef?: any,
}

export interface ReloadProps {
  /* 子元素 */
  children?: any;
  /* 刷新数据回调 */
  onReload?: () => void
}

interface ToolbarState {
  showBatch: boolean,
  selectedListPre: any[],
}
export class Toolbar extends PureComponent<ToolbarProps, ToolbarState> {
  static defaultProps = {};
  static TOOL_TYPE = {
    filter: 'filter',
    columns: 'columns',
    batch: 'batch',
    download: 'download',
    separator: 'separator',
    reload: 'reload',
  };
  static getDerivedStateFromProps(nextProps: any, prevState: any) {
    // 选择数量大于0展示批量操作
    const { selectedList = [] } = nextProps.batchOptions;
    if (!_.isEqual(prevState.selectedListPre, selectedList)) {
      const showBatch = selectedList.length > 0;
      return {
        ...prevState,
        showBatch,
        selectedListPre: selectedList,
      };
    }
    return null;
  }
  constructor(props: ToolbarProps) {
    super(props);
    this.state = {
      showBatch: false,
      selectedListPre: [],
    };
  }

  componentDidMount() { }

  renderRight = () => {
    const { right, columnsOptions, downloadOptions, reloadOptions, listRef } = this.props;
    if (!_.isArray(right)) {
      return right;
    }
    const rightElement = _.filter(right, (v) => v).map((v, i) => {
      // 筛选
      if (v === Toolbar.TOOL_TYPE.filter) {
        return (
          <Button className="borderless" key="filter" data-report="toolbar.filter">
            <i className="toolbar-icon spaui-icon icon-filter" />
            筛选
          </Button>
        );
      }
      // 自定义列
      if (v === Toolbar.TOOL_TYPE.columns) {
        return columnsOptions ? (
          <GuideTip
            key={FLAGS.AREA}
            markKey={FLAGS.AREA}
            title={'自定义列'}
            content={<div>自定义列表增加字段：k框和广告主集团字段</div>}
            placement={'bottom'}
            expires={'2022-03-20'}
          >
            <CustomColumn {...columnsOptions} key="columns" />
          </GuideTip>
        ) : null;
      }
      // 批量操作
      if (v === Toolbar.TOOL_TYPE.batch) {
        return (
          <Button
            className="borderless"
            onClick={() => this.setState({ showBatch: true })}
            key="batch"
            data-report="toolbar.batch"
          >
            <i className="toolbar-icon spaui-icon icon-batch" />
            批量操作
          </Button>
        );
      }
      // 下载数据
      if (v === Toolbar.TOOL_TYPE.download) {
        return <Download {...downloadOptions} key="download" />;
      }
      // 分割符
      if (v === Toolbar.TOOL_TYPE.separator) {
        return <div className="separator" key="separator"></div>;
      }
      // 刷新列表
      if (v === Toolbar.TOOL_TYPE.reload) {
        return (
          <Button
            className="borderless"
            key="reload"
            onClick={reloadOptions?.onReload || (() => listRef?.current?.loadList())}
            data-report="toolbar.refresh"
          >
            {reloadOptions?.children || <>
              <i className="toolbar-icon spaui-icon icon-refresh" />
              刷新列表
            </>}
          </Button>
        );
      }
      return <React.Fragment key={i}>{v}</React.Fragment>;
    });
    return rightElement;
  };

  render() {
    const { className, left, leftClassName, rightClassName, batchOptions } = this.props;
    const { showBatch } = this.state;

    const rightElement = this.renderRight();

    return (
      <div className={classNames('base-toolbar-container', className)}>
        <PTPToolbar className="base-toolbar" left={left} right={rightElement} leftClassName={leftClassName} rightClassName={rightClassName} />
        {batchOptions ? <BatchToolbar {...batchOptions} show={showBatch} onClose={() => this.setState({ showBatch: false })} /> : null}
      </div>
    );
  }
}

Toolbar.defaultProps = {
  columnsOptions: {},
  batchOptions: {},
  downloadOptions: {},
};

export default Toolbar;
